import { useEffect, useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { MdOutlineClose, MdWarningAmber, MdAutoFixHigh } from "react-icons/md";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import GSTConnectionModalShell from "./GSTConnectionModalShell";
import gstStyles from "../../pages/GSTReconciliation/GSTReconciliation.module.scss";
import styles from "./GSTUploadListModal.module.scss";

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ["xlsx", "xls", "csv"];
// The import runs async on the backend (S3 → Lambda → callback); if no socket
// update lands within this window, surface an error instead of spinning forever.
export const DEFAULT_IMPORT_TIMEOUT_MS = 3 * 60 * 1000;

const DEFAULT_SAMPLE_FILE_URL =
  "https://docs.google.com/spreadsheets/d/1HgAqZ1uSc9NwMeqkpY-V9ZNkH_xI7I9Pc84fanvfud0/edit?usp=sharing";

interface GSTUploadListModalProps {
  title?: string;
  subtitle?: string;
  importing: boolean;
  readError: boolean;
  fileError: string;
  sampleFileUrl?: string;
  onClose: () => void;
  onImport: (file: File) => void;
  onClearError: () => void;
}

export default function GSTUploadListModal({
  title = "Import your GST accounts",
  subtitle = "Add all your GST accounts at once.",
  importing = false,
  readError = false,
  fileError = "",
  sampleFileUrl = DEFAULT_SAMPLE_FILE_URL,
  onClose,
  onImport,
  onClearError,
}: GSTUploadListModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [localFileError, setLocalFileError] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!importing && !readError) setLocalFileError("");
  }, [importing, readError]);

  const stageFile = (f: File | undefined | null) => {
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setLocalFileError(`"${f.name}" is not supported. Upload an xlsx, xls or csv file.`);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setLocalFileError(`"${f.name}" exceeds the 50 MB limit.`);
      return;
    }
    setLocalFileError("");
    onClearError();
    setFile(f);
  };

  const downloadSampleFile = () => {
    if (sampleFileUrl) window.open(sampleFileUrl, "_blank", "noopener,noreferrer");
  };

  const effectiveFileError = localFileError || fileError;

  return (
    <GSTConnectionModalShell
      onOverlayClick={importing ? undefined : onClose}
      title={title}
      subtitle={subtitle}
      footer={
        <>
          <MeritoButton
            type="secondary-2"
            size="lg"
            isRounded
            btnText="Close"
            disabled={importing}
            onClick={onClose}
          />
          <MeritoButton
            type="primary-1"
            size="lg"
            isRounded
            btnText={importing ? "Reading file" : "Import Accounts"}
            loading={importing}
            showLoadingWithText
            disabled={!file || importing || readError}
            extraStyles={
              !file || importing || readError
                ? { backgroundColor: "#808080", borderColor: "#808080", opacity: 1 }
                : undefined
            }
            onClick={() => {
              if (file) onImport(file);
            }}
          />
        </>
      }
    >
      <div className={gstStyles.aiMapBanner}>
        <div className={gstStyles.aiMapBannerIcon}>
          <MdAutoFixHigh />
        </div>
        <span className={gstStyles.aiMapBannerText}>
          Upload your file as-is. We will find the columns. Include <strong>GSTIN</strong> and <strong>Username</strong> columns in your file.
        </span>
      </div>

      {!file ? (
        <div
          className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            stageFile(e.dataTransfer.files?.[0]);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={(e) => {
              stageFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <div className={styles.dzContent}>
            <div className={styles.dzIconCircle}>
              <FiUpload size={15} />
            </div>
            <div className={styles.dzTitle}>Drop files here or click to browse</div>
            <div className={styles.dzHint}>
              Should have GSTIN and username.
              <br />
            </div>
          </div>
          <div className={styles.chips}>
            {ACCEPTED_EXTENSIONS.map((ext) => (
              <span key={ext} className={styles.chip}>
                {ext}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.fileTable}>
          <div className={styles.fileHeader}>Uploaded File</div>
          <div className={styles.fileRow}>
            <span className={styles.fileName}>{file.name}</span>
            <MeritoButton
              type="tertiary"
              size="md"
              onlyIcon
              icon={<MdOutlineClose />}
              disabled={importing}
              onClick={() => {
                setFile(null);
                onClearError();
              }}
            />
          </div>
        </div>
      )}

      {effectiveFileError && (
        <div className={gstStyles.uploadInlineError}>{effectiveFileError}</div>
      )}

      {readError && (
        <div className={styles.readError}>
          <div className={styles.readErrorHead}>
            <MdWarningAmber size={18} />
            <span>Couldn't read your file.</span>
          </div>
          <div className={styles.readErrorBody}>
            Make sure it has <strong>GSTIN</strong> and <strong>username</strong> columns.{" "}
            {sampleFileUrl && (
              <span className={styles.readErrorLink} onClick={downloadSampleFile}>
                View sample file →
              </span>
            )}
          </div>
        </div>
      )}
    </GSTConnectionModalShell>
  );
}